import { AgeGroup, Unit } from "../../../constants/exportsCountries"
import {
  convertQuantityToBaseUnit,
  removeEmptyStringValuesFromObject,
} from "../IndiaLabelPreview/NutritionIndiaHelper"
import { roundToNearest } from "../USALabelPreview/NutritionUSAhelper"
import {
  CANADA_NUTRITION_KEYS,
  CanadaNonMandatoryInterleavedNutrient,
  CanadaMandatoryNutrientObject,
  CANADA_NO_DV_NUTRIENT_KEYS,
  CANADA_NUTRIENTS_DV_FROM_ORIGINAL_SERVE,
  CANADA_DV_VALUES,
  CanadaLabelJson,
  INTERLEAVED_NUTRIENTS,
  CanadaMandatoryNutrientData,
  CanadaInterLeavedNonMandatoryNutrientData,
  CanadaNutrientData,
  CanadaNutritionTableValues,
  CANADA_NON_MANDATORY_NUTRIENT_SEQUENCE,
} from "./CanadaTypes"

export function calculateCanadaNutritionTableValues(
  label_json: CanadaLabelJson,
): CanadaNutritionTableValues {
  const {
    serving_size = "",
    serving_size_unit = "g",
    mandatory_nutrient,
    non_mandatory_nutrient = [],
    household_quanity = "",
    translated_household_quantity = "",
    age_group,
  } = label_json

  const serving_size_in_base_unit = convertQuantityToBaseUnit(
    serving_size,
    serving_size_unit,
  )

  const rounded_serving_size = roundOffCanadaServingSize(
    serving_size_in_base_unit,
  )

  const calories = calculateAndRoundOffCanadaCalories(
    mandatory_nutrient?.[0]?.[CANADA_NUTRITION_KEYS.ENERGY] || "",
    serving_size_in_base_unit,
  )

  const filtered_mandatory_nutrient: CanadaMandatoryNutrientObject =
    removeEmptyStringValuesFromObject(mandatory_nutrient[0])

  const mandatory_nutrient_data: CanadaMandatoryNutrientData =
    getCanadaMandatoryNutrientData(
      filtered_mandatory_nutrient,
      serving_size_in_base_unit,
      age_group,
    )

  const interleaved_nutrient_data: CanadaInterLeavedNonMandatoryNutrientData =
    getCanadaNonMandatoryInterLeavedData(
      serving_size_in_base_unit,
      non_mandatory_nutrient,
    )

  const non_mandatory_nutrient_data: CanadaNutrientData[] =
    getCanadaNonMandatoryNutrientData(
      non_mandatory_nutrient,
      serving_size_in_base_unit,
      age_group,
    )

  const sorted_non_mandatory_nutrient_data = sortNonMandatoryNutrientsData(
    non_mandatory_nutrient_data,
    CANADA_NON_MANDATORY_NUTRIENT_SEQUENCE,
  )

  return {
    calories,
    household_quantity_in_english: household_quanity,
    household_quantity_in_french: translated_household_quantity,
    serving_size: rounded_serving_size,
    serving_size_unit: serving_size_unit === "ml" ? "mL" : serving_size_unit,
    mandatory_nutrient: mandatory_nutrient_data,
    interleaved_nutrient: interleaved_nutrient_data,
    non_mandatory_nutrient: sorted_non_mandatory_nutrient_data,
  }
}

export function roundOffCanadaServingSize(serving_size_in_base_unit: number) {
  if (serving_size_in_base_unit < 10) {
    //if it is less than 10 g or 10 mL, to the nearest multiple of 0.1 g or 0.1 mL; Round to nearest 0.1
    return Math.round(serving_size_in_base_unit * 10) / 10
  }
  // if it is 10 g or more or 10 mL or more, round to nearest 1
  return Math.round(serving_size_in_base_unit)
}

export function convertUnitToBaseUnit(unit: Unit): "ml" | "g" {
  const lower_case_unit = unit.toLowerCase()

  if (lower_case_unit === "ml" || lower_case_unit === "l") {
    return "ml"
  }

  return "g"
}

export function calculateAndRoundOffCanadaServingPerContainer(
  serving_size: string | null | undefined,
  serving_size_unit: Unit | null | undefined,
  net_quantity: string | null | undefined,
  net_quantity_unit: Unit | null | undefined,
): number | string {
  if (
    !serving_size ||
    !serving_size_unit ||
    !net_quantity ||
    !net_quantity_unit
  ) {
    return ""
  }

  const serving_size_num = parseFloat(serving_size)
  const net_quantity_num = parseFloat(net_quantity)

  if (isNaN(serving_size_num) || isNaN(net_quantity_num)) {
    return ""
  }

  const serving_size_in_base_unit = convertQuantityToBaseUnit(
    serving_size_num,
    serving_size_unit,
  )
  const net_quantity_in_base_unit = convertQuantityToBaseUnit(
    net_quantity_num,
    net_quantity_unit,
  )

  const serving_per_container =
    net_quantity_in_base_unit / serving_size_in_base_unit

  if (
    isFinite(serving_per_container) === false ||
    isNaN(serving_per_container)
  ) {
    return ""
  }

  let rounded: number
  let is_rounded = false

  if (serving_per_container < 2 || serving_per_container > 5) {
    // If number of servings < 2 or > 5, then round to the nearest whole number and use “about.”
    const rounded_whole = Math.round(serving_per_container)
    is_rounded = rounded_whole !== serving_per_container
    rounded = rounded_whole
  } else {
    // If number of servings is ≥ 2 and ≤ 5, then round to the nearest 0.5 and use “about.”
    const rounded_half = Math.round(serving_per_container * 2) / 2
    is_rounded = rounded_half !== serving_per_container
    rounded = rounded_half
  }

  return is_rounded ? `about ${rounded}` : rounded
}

export function calculateAndRoundOffCanadaCalories(
  energy_per_100g_value: number | string,
  serving_size_in_base_unit: number,
): number | string {
  const parsed_energy =
    typeof energy_per_100g_value === "string"
      ? parseFloat(energy_per_100g_value)
      : energy_per_100g_value

  // Validate inputs: empty, null, undefined, NaN, Infinity
  if (
    parsed_energy === undefined ||
    serving_size_in_base_unit === undefined ||
    parsed_energy === null ||
    serving_size_in_base_unit === null ||
    isNaN(parsed_energy) ||
    isNaN(serving_size_in_base_unit) ||
    !isFinite(parsed_energy) ||
    !isFinite(serving_size_in_base_unit)
  ) {
    return ""
  }

  // Compute calories per serve
  const calories_per_serve = (parsed_energy * serving_size_in_base_unit) / 100

  if (!isFinite(calories_per_serve) || isNaN(calories_per_serve)) {
    return ""
  }

  // If calories per serving < 5, then declare as 0 calories.
  if (calories_per_serve < 5) {
    return 0
  }

  // If calories per serving ≥ 5 and ≤ 50, then round to the nearest 5-calorie increment.
  if (calories_per_serve <= 50) {
    return roundToNearest(calories_per_serve, 5)
  }

  // If calories per serving > 50, then round to the nearest 10-calorie increment.
  return roundToNearest(calories_per_serve, 10)
}

export function calculateAndRoundOffCanadaMandatoryNutrientPerServe(
  nutrient_key: string,
  serving_size_in_base_unit: number,
  mandatory_nutrient: [CanadaMandatoryNutrientObject],
): number | string {
  const per_100g_value = mandatory_nutrient?.[0]?.[nutrient_key] ?? 0

  const parse_per_100g_value =
    typeof per_100g_value === "string"
      ? parseFloat(per_100g_value)
      : per_100g_value

  if (
    parse_per_100g_value === undefined ||
    serving_size_in_base_unit === undefined ||
    parse_per_100g_value === null ||
    serving_size_in_base_unit === null ||
    isNaN(parse_per_100g_value) ||
    isNaN(serving_size_in_base_unit) ||
    !isFinite(parse_per_100g_value) ||
    !isFinite(serving_size_in_base_unit)
  ) {
    return ""
  }

  const nutrient_per_serve =
    (parse_per_100g_value * serving_size_in_base_unit) / 100

  if (!isFinite(nutrient_per_serve) || isNaN(nutrient_per_serve)) {
    return ""
  }

  const total_fat_per_100g =
    mandatory_nutrient?.[0]?.[CANADA_NUTRITION_KEYS.TOTAL_FAT]
  const saturated_fat_per_100g =
    mandatory_nutrient?.[0]?.[CANADA_NUTRITION_KEYS.SATURATED_FAT]
  const trans_fat_per_100g =
    mandatory_nutrient?.[0]?.[CANADA_NUTRITION_KEYS.TRANS_FAT]

  if (nutrient_key === CANADA_NUTRITION_KEYS.TOTAL_FAT) {
    return calculateAndRoundOffCanadaTotalFatPerServe(
      total_fat_per_100g,
      saturated_fat_per_100g,
      trans_fat_per_100g,
      serving_size_in_base_unit,
    )
  }

  if (nutrient_key === CANADA_NUTRITION_KEYS.SATURATED_FAT) {
    return calculateAndRoundOffCanadaSaturatedFatPerServe(
      saturated_fat_per_100g,
      trans_fat_per_100g,
      serving_size_in_base_unit,
    )
  }

  if (nutrient_key === CANADA_NUTRITION_KEYS.TRANS_FAT) {
    return calculateAndRoundOffCanadaTransFatPerServe(
      saturated_fat_per_100g,
      trans_fat_per_100g,
      serving_size_in_base_unit,
    )
  }

  if (
    [
      CANADA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
      CANADA_NUTRITION_KEYS.DIETARY_FIBRE,
      CANADA_NUTRITION_KEYS.TOTAL_SUGARS,
    ]?.includes(nutrient_key)
  ) {
    // If carbohydrate , dietary fibre, total sugar per serve <0.5g, round off to 0g.
    if (nutrient_per_serve < 0.5) {
      return 0
    }
    // if carbohydrate , dietary fibre,total sugar per serve >=0.5g round off to nearest multiple of 1 g
    return Math.round(nutrient_per_serve)
  }

  if (nutrient_key === CANADA_NUTRITION_KEYS.PROTEIN) {
    // If Protein per serve <0.5g, round to nearest multiple of 0.1g
    if (nutrient_per_serve < 0.5) {
      return Math.round(nutrient_per_serve * 10) / 10
    }
    // If Protein per serve >= 0.5g, round to nearest multiple of 1 g
    return Math.round(nutrient_per_serve)
  }

  if (nutrient_key === CANADA_NUTRITION_KEYS.CHOLESTEROL) {
    // If cholesterol per serve <2mg, round to 0 g
    if (nutrient_per_serve < 2) {
      return 0
    }

    // If cholesterol per serve >2mg, round to nearest multiple of 5 mg.
    return roundToNearest(nutrient_per_serve, 5)
  }

  if (nutrient_key === CANADA_NUTRITION_KEYS.SODIUM) {
    // If sodium per serve <5mg, round to 0 mg
    if (nutrient_per_serve < 5) {
      return 0
    }

    // If sodium per serve >=5mg and <=140mg, round to nearest multiple of 5 mg.
    if (nutrient_per_serve <= 140) {
      return roundToNearest(nutrient_per_serve, 5)
    }

    // If sodium per serve >140mg, round to nearest multiple of 10 mg.
    return roundToNearest(nutrient_per_serve, 10)
  }

  if (nutrient_key === CANADA_NUTRITION_KEYS.POTASSIUM) {
    // If potassium per serve <5mg, round to 0 mg
    if (nutrient_per_serve < 5) {
      return 0
    }

    // If potassium per serve >=5mg and <50mg, round to nearest multiple of 10 mg.
    if (nutrient_per_serve < 50) {
      return roundToNearest(nutrient_per_serve, 10)
    }

    // If potassium per serve >=50mg and <250mg, round to nearest multiple of 25 mg.
    if (nutrient_per_serve < 250) {
      return roundToNearest(nutrient_per_serve, 25)
    }

    // If potassium per serve >=250mg, round to nearest multiple of 50 mg.
    return roundToNearest(nutrient_per_serve, 50)
  }

  if (nutrient_key === CANADA_NUTRITION_KEYS.CALCIUM) {
    // If calcium per serve <5mg, round to 0 mg
    if (nutrient_per_serve < 5) {
      return 0
    }

    // If calcium per serve >=5mg and <50mg, round to nearest multiple of 10 mg.
    if (nutrient_per_serve < 50) {
      return roundToNearest(nutrient_per_serve, 10)
    }

    // If calcium per serve >=50mg and <250mg, round to nearest multiple of 25 mg.
    if (nutrient_per_serve < 250) {
      return roundToNearest(nutrient_per_serve, 25)
    }

    // If calcium per serve >=250mg, round to nearest multiple of 50 mg.
    return roundToNearest(nutrient_per_serve, 50)
  }

  if (nutrient_key === CANADA_NUTRITION_KEYS.IRON) {
    // If iron per serve <0.05mg, round to 0 mg
    if (nutrient_per_serve < 0.05) {
      return 0
    }

    // If iron per serve >=0.05mg and <0.5mg, round to nearest multiple of 0.1 mg.
    if (nutrient_per_serve < 0.5) {
      return Math.round(nutrient_per_serve * 10) / 10
    }

    // If iron per serve >=0.5mg and <2.5mg, round to nearest multiple of 0.25 mg.
    if (nutrient_per_serve < 2.5) {
      return Math.round(nutrient_per_serve * 4) / 4
    }

    // If iron per serve >=2.5mg, round to nearest multiple of 0.5 mg.
    return Math.round(nutrient_per_serve * 2) / 2
  }

  // Default: round to 0.1 g
  return Math.round(nutrient_per_serve * 10) / 10
}

/**
 * This function does not calculate MUFA, PUFA, Omega-3, or Omega-6.
 * Use calculateAndRoundOffCanadaNonMandatoryInterLeavedNutrientPerServe() to retrieve the data for the above nutrients.
 */
export function calculateAndRoundOffCanadaNonMandatoryNutrientPerServe(
  nutrient_key: string,
  serve_size_in_base_unit: number,
  per_100g_value: string | number,
): number | string {
  const parse_per_100g_value =
    typeof per_100g_value === "string"
      ? parseFloat(per_100g_value)
      : per_100g_value

  if (
    parse_per_100g_value === undefined ||
    serve_size_in_base_unit === undefined ||
    parse_per_100g_value === null ||
    serve_size_in_base_unit === null ||
    isNaN(parse_per_100g_value) ||
    isNaN(serve_size_in_base_unit) ||
    !isFinite(parse_per_100g_value) ||
    !isFinite(serve_size_in_base_unit)
  ) {
    return ""
  }

  const nutrient_per_serve =
    (parse_per_100g_value * serve_size_in_base_unit) / 100

  if (!isFinite(nutrient_per_serve) || isNaN(nutrient_per_serve)) {
    return ""
  }

  if (nutrient_key === CANADA_NUTRITION_KEYS.VITAMIN_A) {
    // If Vitamin A per serve <5µg, round to 0 µg
    if (nutrient_per_serve < 5) {
      return 0
    }

    // If Vitamin A per serve >=5µg and <50µg, round to nearest multiple of 10 µg.
    if (nutrient_per_serve < 50) {
      return roundToNearest(nutrient_per_serve, 10)
    }

    // If Vitamin A per serve >=50µg and <250µg, round to nearest multiple of 50 µg.
    if (nutrient_per_serve < 250) {
      return roundToNearest(nutrient_per_serve, 50)
    }

    // If Vitamin A per serve >=250µg, round to nearest multiple of 100 µg.
    return roundToNearest(nutrient_per_serve, 100)
  }

  if (
    [
      CANADA_NUTRITION_KEYS.VITAMIN_C,
      CANADA_NUTRITION_KEYS.VITAMIN_D,
      CANADA_NUTRITION_KEYS.SELENIUM,
    ].includes(nutrient_key)
  ) {
    // If nutrient per serve <0.1mg, round to 0 mg
    if (nutrient_per_serve < 0.1) {
      return 0
    }

    // If nutrient per serve >=0.1mg and <1mg, round to nearest multiple of 0.2 mg.
    if (nutrient_per_serve < 1) {
      return Number((Math.round(nutrient_per_serve / 0.2) * 0.2).toFixed(2))
    }

    // If nutrient per serve >=1mg and <5mg, round to nearest multiple of 0.5 mg.
    if (nutrient_per_serve < 5) {
      return Math.round(nutrient_per_serve * 2) / 2
    }

    // If nutrient per serve >=5mg, round to nearest multiple of 1 mg.
    return roundToNearest(nutrient_per_serve, 1)
  }

  if (
    [
      CANADA_NUTRITION_KEYS.VITAMIN_E,
      CANADA_NUTRITION_KEYS.VITAMIN_B3,
      CANADA_NUTRITION_KEYS.ZINC,
      CANADA_NUTRITION_KEYS.VITAMIN_K1,
      CANADA_NUTRITION_KEYS.BIOTIN,
      CANADA_NUTRITION_KEYS.CHROMIUM,
      CANADA_NUTRITION_KEYS.MOLYBDENUM,
    ].includes(nutrient_key)
  ) {
    // If nutrient per serve <0.05mg, round to 0 mg
    if (nutrient_per_serve < 0.05) {
      return 0
    }

    // If nutrient per serve >=0.05mg and <0.5mg, round to nearest multiple of 0.1 mg.
    if (nutrient_per_serve < 0.5) {
      return Math.round(nutrient_per_serve * 10) / 10
    }

    // If nutrient per serve >=0.5mg and <2.5mg, round to nearest multiple of 0.25 mg.
    if (nutrient_per_serve < 2.5) {
      return Math.round(nutrient_per_serve * 4) / 4
    }

    // If nutrient per serve >=2.5mg, round to nearest multiple of 0.5 mg.
    return Math.round(nutrient_per_serve * 2) / 2
  }

  if (
    [
      CANADA_NUTRITION_KEYS.VITAMIN_B1,
      CANADA_NUTRITION_KEYS.VITAMIN_B2,
      CANADA_NUTRITION_KEYS.VITAMIN_B6,
      CANADA_NUTRITION_KEYS.VITAMIN_B12,
      CANADA_NUTRITION_KEYS.VITAMIN_B1,
    ].includes(nutrient_key)
  ) {
    // If nutrient per serve <0.005mg, round to 0 mg
    if (nutrient_per_serve < 0.005) {
      return 0
    }

    // If nutrient per serve >=0.005mg and <0.05mg, round to nearest multiple of 0.01 mg.
    if (nutrient_per_serve < 0.05) {
      return Number((Math.round(nutrient_per_serve / 0.01) * 0.01).toFixed(3))
    }

    // If nutrient per serve >=0.05mg and <0.25mg, round to nearest multiple of 0.025 mg.
    if (nutrient_per_serve < 0.25) {
      return Number((Math.round(nutrient_per_serve / 0.025) * 0.025).toFixed(3))
    }

    // If nutrient per serve >=0.25mg, round to nearest multiple of 0.05 mg.
    return Number((Math.round(nutrient_per_serve / 0.05) * 0.05).toFixed(3))
  }

  if (
    [CANADA_NUTRITION_KEYS.FOLIC_ACID, CANADA_NUTRITION_KEYS.CHOLINE].includes(
      nutrient_key,
    )
  ) {
    // If nutrient per serve <1mg, round to 0 mg
    if (nutrient_per_serve < 1) {
      return 0
    }

    // If nutrient per serve >=1mg and <10mg, round to nearest multiple of 2 mg.
    if (nutrient_per_serve < 10) {
      return roundToNearest(nutrient_per_serve, 2)
    }

    // If nutrient per serve >=10mg and <50mg, round to nearest multiple of 5 mg.
    if (nutrient_per_serve < 50) {
      return roundToNearest(nutrient_per_serve, 5)
    }

    // If nutrient per serve >=50mg, round to nearest multiple of 10 mg.
    return roundToNearest(nutrient_per_serve, 10)
  }

  if (nutrient_key === CANADA_NUTRITION_KEYS.PANTOTHENIC_ACID) {
    // (a) If amount is < 0.01 mg per serve, then it is rounded to "0 mg"
    if (nutrient_per_serve < 0.01) {
      return 0
    }

    // (b) If amount is ≥ 0.01 mg and < 0.1 mg per serve, round to nearest multiple of 0.02 mg
    if (nutrient_per_serve < 0.1) {
      return Number((Math.round(nutrient_per_serve / 0.02) * 0.02).toFixed(3))
    }

    // (c) If amount is ≥ 0.1 mg and < 0.5 mg per serve, round to nearest multiple of 0.05 mg
    if (nutrient_per_serve < 0.5) {
      return Number((Math.round(nutrient_per_serve / 0.05) * 0.05).toFixed(3))
    }

    // (d) If amount is ≥ 0.5 mg per serve, round to nearest multiple of 0.1 mg
    return Math.round(nutrient_per_serve * 10) / 10
  }

  if (
    [
      CANADA_NUTRITION_KEYS.MAGNESIUM,
      CANADA_NUTRITION_KEYS.CHOLINE,
      CANADA_NUTRITION_KEYS.IODINE,
    ].includes(nutrient_key)
  ) {
    // (a) If amount is < 1 mg per serve, then it is rounded to "0 mg"
    if (nutrient_per_serve < 1) {
      return 0
    }

    // (b) If amount is ≥ 1 mg and < 10 mg per serve, round to nearest multiple of 2 mg
    if (nutrient_per_serve < 10) {
      return Math.round(nutrient_per_serve / 2) * 2
    }

    // (c) If amount is ≥ 10 mg and < 50 mg per serve, round to nearest multiple of 5 mg
    if (nutrient_per_serve < 50) {
      return Math.round(nutrient_per_serve / 5) * 5
    }

    // (d) If amount is ≥ 50 mg per serve, round to nearest multiple of 10 mg
    return Math.round(nutrient_per_serve / 10) * 10
  }

  if (
    [CANADA_NUTRITION_KEYS.PHOSPHORUS, CANADA_NUTRITION_KEYS.CHLORIDE].includes(
      nutrient_key,
    )
  ) {
    // If nutrient per serve <5mg, round to 0 mg
    if (nutrient_per_serve < 5) {
      return 0
    }

    // If nutrient per serve >=5mg and <50mg, round to nearest multiple of 10 mg.
    if (nutrient_per_serve < 50) {
      return roundToNearest(nutrient_per_serve, 10)
    }

    // If nutrient per serve >=50mg and <250mg, round to nearest multiple of 25 mg.
    if (nutrient_per_serve < 250) {
      return roundToNearest(nutrient_per_serve, 25)
    }

    // If nutrient per serve >=250mg, round to nearest multiple of 50 mg.
    return roundToNearest(nutrient_per_serve, 50)
  }

  if (nutrient_key === CANADA_NUTRITION_KEYS.COPPER) {
    // If nutrient per serve <0.0015mg, round to 0 mg
    if (nutrient_per_serve < 0.0015) {
      return 0
    }

    // If nutrient per serve >=0.0015mg and <0.025mg, round to nearest multiple of 0.002 mg.
    if (nutrient_per_serve < 0.025) {
      return Number((Math.round(nutrient_per_serve / 0.002) * 0.002).toFixed(3))
    }

    // If nutrient per serve >=0.025mg and <0.05mg, round to nearest multiple of 0.005 mg.
    if (nutrient_per_serve < 0.05) {
      return Number((Math.round(nutrient_per_serve / 0.005) * 0.005).toFixed(3))
    }

    // If nutrient per serve >=0.05mg, round to nearest multiple of 0.01 mg.
    return Number((Math.round(nutrient_per_serve / 0.01) * 0.01).toFixed(3))
  }

  if (nutrient_key === CANADA_NUTRITION_KEYS.MANGANESE) {
    // If amount is < 0.005 mg per serve, then it is rounded to "0 mg"
    if (nutrient_per_serve < 0.005) {
      return 0
    }

    // If amount is ≥ 0.005 mg and < 0.05 mg per serve, then it is rounded to the nearest multiple of 0.01 mg
    if (nutrient_per_serve < 0.05) {
      return Number((Math.round(nutrient_per_serve / 0.01) * 0.01).toFixed(2))
    }
    // If amount is ≥ 0.05 mg and < 0.25 mg per serve, then it is rounded to the nearest multiple of 0.025 mg
    if (nutrient_per_serve < 0.25) {
      return Number((Math.round(nutrient_per_serve / 0.025) * 0.025).toFixed(3))
    }

    // If amount is ≥ 0.25 mg per serve, then it is rounded to the nearest multiple of 0.05 mg
    return Number((Math.round(nutrient_per_serve / 0.05) * 0.05).toFixed(2))
  }

  // Default case round to 0.1g
  return Number((Math.round(nutrient_per_serve / 0.1) * 0.1).toFixed(2))
}

export function calculateAndRoundOffCanadaNonMandatoryInterLeavedNutrientPerServe(
  nutrient_key: CanadaNonMandatoryInterleavedNutrient,
  serving_size_in_base_unit: number,
  non_mandatory_nutrient: { name: string; quantity: string }[],
) {
  const filtered_non_mandatory_nutrient = non_mandatory_nutrient?.filter(
    (nutrient) =>
      nutrient.name?.trim() &&
      nutrient.quantity !== undefined &&
      nutrient.quantity !== null &&
      nutrient.quantity.trim() !== "",
  )

  const nutrient_data = filtered_non_mandatory_nutrient?.find(
    (nutrient) => nutrient?.name?.toLowerCase() === nutrient_key?.toLowerCase(),
  )

  const per_100g_value = nutrient_data?.quantity ?? ""

  const parse_per_100g_value =
    typeof per_100g_value === "string"
      ? parseFloat(per_100g_value)
      : per_100g_value

  const nutrient_per_serve =
    (parse_per_100g_value * serving_size_in_base_unit) / 100

  // In Canada,all interleaved nutrients have the same rounding rules

  if (
    [
      CANADA_NUTRITION_KEYS.PUFA,
      CANADA_NUTRITION_KEYS.MUFA,
      CANADA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS,
      CANADA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS,
    ].includes(nutrient_key)
  ) {
    // If the amount per serve is < 1 g Round to the nearest multiple of 0.1 g
    if (nutrient_per_serve < 1) {
      return Math.round(nutrient_per_serve * 10) / 10
    }

    // If the amount per serve is ≥ 1 g and ≤ 5 g Round to the nearest multiple of 0.5 g
    if (nutrient_per_serve >= 1 && nutrient_per_serve <= 5) {
      return Math.round(nutrient_per_serve * 2) / 2
    }
    // If the amount per serve is > 5 g Round to the nearest multiple of 1 g
    return Math.round(nutrient_per_serve)
  }

  return Math.round(nutrient_per_serve * 10) / 10
}

export function sumOfCanadaTransFatAndSaturatedFat(
  mandatory_nutrient: [CanadaMandatoryNutrientObject],
): number {
  const trans_fat_value =
    mandatory_nutrient?.[0]?.[CANADA_NUTRITION_KEYS.TRANS_FAT] ?? 0
  const saturated_fat_value =
    mandatory_nutrient?.[0]?.[CANADA_NUTRITION_KEYS.SATURATED_FAT] ?? 0

  const parse_trans_fat_value =
    typeof trans_fat_value === "string"
      ? parseFloat(trans_fat_value)
      : trans_fat_value

  const parse_saturated_fat_value =
    typeof saturated_fat_value === "string"
      ? parseFloat(saturated_fat_value)
      : saturated_fat_value

  return parse_trans_fat_value + parse_saturated_fat_value
}

export function calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
  nutrient_key: string,
  serving_size_in_base_unit: number,
  mandatory_nutrient: [CanadaMandatoryNutrientObject],
  age_group: AgeGroup,
): number | string {
  if (CANADA_NO_DV_NUTRIENT_KEYS.includes(nutrient_key)) {
    return ""
  }

  let nutrient_per_serve =
    calculateAndRoundOffCanadaMandatoryNutrientPerServe(
      nutrient_key,
      serving_size_in_base_unit,
      mandatory_nutrient,
    ) ?? 0

  if (CANADA_NUTRIENTS_DV_FROM_ORIGINAL_SERVE.includes(nutrient_key)) {
    let per_100g_value: number | string =
      mandatory_nutrient?.[0]?.[nutrient_key] ?? 0

    if (
      [
        CANADA_NUTRITION_KEYS.TRANS_FAT,
        CANADA_NUTRITION_KEYS.SATURATED_FAT,
      ]?.includes(nutrient_key)
    ) {
      per_100g_value = sumOfCanadaTransFatAndSaturatedFat(mandatory_nutrient)
    }

    const parse_per_100g_value =
      typeof per_100g_value === "string"
        ? parseFloat(per_100g_value)
        : per_100g_value

    if (isNaN(parse_per_100g_value)) return ""

    nutrient_per_serve =
      (parse_per_100g_value * serving_size_in_base_unit) / 100
  }

  const dv_value: number | null =
    CANADA_DV_VALUES?.[age_group as keyof typeof CANADA_DV_VALUES]?.[
      nutrient_key
    ]?.daily_value

  const parse_nutrient_per_serve =
    typeof nutrient_per_serve === "string"
      ? parseFloat(nutrient_per_serve)
      : nutrient_per_serve

  if (
    !dv_value ||
    isNaN(dv_value) ||
    parse_nutrient_per_serve === undefined ||
    isNaN(parse_nutrient_per_serve)
  ) {
    return ""
  }

  // daily value == (per_serve/dv_values)*100
  const percentDV = (parse_nutrient_per_serve / dv_value) * 100

  if (isNaN(percentDV) || !isFinite(percentDV)) {
    return ""
  }

  if (percentDV == 0) {
    return 0
  }

  // Round to nearest whole number
  return Math.round(percentDV)
}

export function calculateAndRoundOffCanadaNonMandatoryNutrientDailyValue(
  per_100g_value: string | number,
  nutrient_key: string,
  serve_size_in_base_unit: number,
  age_group: AgeGroup,
): number | string {
  if (CANADA_NO_DV_NUTRIENT_KEYS.includes(nutrient_key)) {
    return ""
  }

  const nutrient_per_serve =
    calculateAndRoundOffCanadaNonMandatoryNutrientPerServe(
      nutrient_key,
      serve_size_in_base_unit,
      per_100g_value,
    )

  const dv_value: number | null =
    CANADA_DV_VALUES?.[age_group as keyof typeof CANADA_DV_VALUES]?.[
      nutrient_key
    ]?.daily_value

  const parse_nutrient_per_serve =
    typeof nutrient_per_serve === "string"
      ? parseFloat(nutrient_per_serve)
      : nutrient_per_serve

  if (
    !dv_value ||
    isNaN(dv_value) ||
    parse_nutrient_per_serve === undefined ||
    isNaN(parse_nutrient_per_serve)
  ) {
    return ""
  }

  // daily value == (per_serve/dv_values)*100
  const percentDV = (parse_nutrient_per_serve / dv_value) * 100

  if (isNaN(percentDV) || !isFinite(percentDV)) {
    return ""
  }

  if (percentDV == 0) {
    return 0
  }

  // Round to nearest whole number
  return Math.round(percentDV)
}

export function calculateAndRoundOffCanadaTotalFatPerServe(
  total_fat_per_100g_value: string | number = 0,
  saturated_fat_per_100g_value: string | number = 0,
  trans_fat_per_100g_value: string | number = 0,
  serve_size_in_base_unit: number,
): number {
  let parse_total_fat_per_100g_value =
    typeof total_fat_per_100g_value === "string"
      ? parseFloat(total_fat_per_100g_value)
      : total_fat_per_100g_value

  let parse_saturated_fat_per_100g_value =
    typeof saturated_fat_per_100g_value === "string"
      ? parseFloat(saturated_fat_per_100g_value)
      : saturated_fat_per_100g_value

  let parse_trans_fat_per_100g_value =
    typeof trans_fat_per_100g_value === "string"
      ? parseFloat(trans_fat_per_100g_value)
      : trans_fat_per_100g_value

  if (isNaN(parse_total_fat_per_100g_value)) {
    parse_total_fat_per_100g_value = 0
  }
  if (isNaN(parse_saturated_fat_per_100g_value)) {
    parse_saturated_fat_per_100g_value = 0
  }
  if (isNaN(parse_trans_fat_per_100g_value)) {
    parse_trans_fat_per_100g_value = 0
  }

  const total_fat_per_serve =
    (parse_total_fat_per_100g_value * serve_size_in_base_unit) / 100

  const saturated_fat_per_serve =
    (parse_saturated_fat_per_100g_value * serve_size_in_base_unit) / 100

  const trans_fat_per_serve =
    (parse_trans_fat_per_100g_value * serve_size_in_base_unit) / 100

  // a) < 0.5 g fat AND both saturated + trans are 0
  if (
    total_fat_per_serve < 0.5 &&
    saturated_fat_per_serve === 0 &&
    trans_fat_per_serve === 0
  ) {
    return 0
  }

  // b) < 0.5 g fat AND saturated OR trans > 0
  if (
    total_fat_per_serve < 0.5 &&
    (saturated_fat_per_serve > 0 || trans_fat_per_serve > 0)
  ) {
    // Round to nearest 0.1 g
    return Math.round(total_fat_per_serve * 10) / 10
  }

  // c) 0.5 g to 5 g → round to nearest 0.5 g
  if (total_fat_per_serve >= 0.5 && total_fat_per_serve <= 5) {
    return Math.round(total_fat_per_serve * 2) / 2
  }

  // d) > 5 g → round to nearest 1 g
  if (total_fat_per_serve > 5) {
    return Math.round(total_fat_per_serve)
  }

  // fallback
  return Math.round(total_fat_per_serve * 10) / 10
}

export function calculateAndRoundOffCanadaSaturatedFatPerServe(
  saturated_fat_per_100g_value: string | number,
  trans_fat_per_100g_value: string | number,
  serve_size_in_base_unit: number,
): number {
  let parse_saturated_fat_per_100g =
    typeof saturated_fat_per_100g_value === "string"
      ? parseFloat(saturated_fat_per_100g_value)
      : saturated_fat_per_100g_value

  let parse_trans_fat_per_100g =
    typeof trans_fat_per_100g_value === "string"
      ? parseFloat(trans_fat_per_100g_value)
      : trans_fat_per_100g_value

  if (isNaN(parse_saturated_fat_per_100g)) {
    parse_saturated_fat_per_100g = 0
  }
  if (isNaN(parse_trans_fat_per_100g)) {
    parse_trans_fat_per_100g = 0
  }

  const saturated_per_serve =
    (parse_saturated_fat_per_100g * serve_size_in_base_unit) / 100

  const trans_per_serve =
    (parse_trans_fat_per_100g * serve_size_in_base_unit) / 100

  // Rule a: both saturated and trans fat per serve < 0.2 g → round to 0
  if (saturated_per_serve < 0.2 && trans_per_serve < 0.2) {
    return 0
  }

  // Rule b: saturated < 0.2g and trans > 0.2g → round to 0.1g
  if (saturated_per_serve < 0.2 && trans_per_serve > 0.2) {
    return Math.round(saturated_per_serve * 10) / 10
  }

  // Rule c: saturated >= 0.2g and < 0.5g → round to nearest 0.1g
  if (saturated_per_serve >= 0.2 && saturated_per_serve < 0.5) {
    return Math.round(saturated_per_serve * 10) / 10
  }

  // Rule d: >= 0.5g to 5g → round to nearest 0.5g
  if (saturated_per_serve >= 0.5 && saturated_per_serve <= 5) {
    return Math.round(saturated_per_serve * 2) / 2
  }

  // Rule e: > 5g → round to nearest 1g
  if (saturated_per_serve > 5) {
    return Math.round(saturated_per_serve)
  }

  // fallback (shouldn’t occur under normal expected input)
  return Math.round(saturated_per_serve * 10) / 10
}

export function calculateAndRoundOffCanadaTransFatPerServe(
  saturated_fat_per_100g_value: string | number,
  trans_fat_per_100g_value: string | number,
  serve_size_in_base_unit: number,
): number {
  let parse_saturated_fat_per_100g_value =
    typeof saturated_fat_per_100g_value === "string"
      ? parseFloat(saturated_fat_per_100g_value)
      : saturated_fat_per_100g_value

  let parse_trans_fat_per_100g_value =
    typeof trans_fat_per_100g_value === "string"
      ? parseFloat(trans_fat_per_100g_value)
      : trans_fat_per_100g_value

  if (isNaN(parse_saturated_fat_per_100g_value)) {
    parse_saturated_fat_per_100g_value = 0
  }
  if (isNaN(parse_trans_fat_per_100g_value)) {
    parse_trans_fat_per_100g_value = 0
  }

  const saturated_per_serve =
    (parse_saturated_fat_per_100g_value * serve_size_in_base_unit) / 100
  const trans_per_serve =
    (parse_trans_fat_per_100g_value * serve_size_in_base_unit) / 100
  const sum_saturated_trans = saturated_per_serve + trans_per_serve

  // Rule a: trans < 0.2 AND (saturated + trans) < 2 → round to 0
  if (trans_per_serve < 0.2 && sum_saturated_trans < 2) {
    return 0
  }

  // Rule b: trans < 0.2 AND (saturated + trans) >= 2 → round to 0.1 g
  if (trans_per_serve < 0.2 && sum_saturated_trans >= 2) {
    return Math.round(trans_per_serve * 10) / 10
  }

  // Rule c: trans >= 0.2 AND <0.5 → round to 0.1 g
  if (trans_per_serve >= 0.2 && trans_per_serve < 0.5) {
    return Math.round(trans_per_serve * 10) / 10
  }

  // Rule d: trans >= 0.5 AND ≤ 5 → round to 0.5 g
  if (trans_per_serve >= 0.5 && trans_per_serve <= 5) {
    return Math.round(trans_per_serve * 2) / 2
  }

  // Rule e: trans > 5 → round to 1 g
  if (trans_per_serve > 5) {
    return Math.round(trans_per_serve)
  }

  // Fallback (shouldn’t be triggered in expected cases)
  return Math.round(trans_per_serve * 10) / 10
}

export function getCanadaMandatoryNutrientData(
  filtered_mandatory_nutrient: CanadaMandatoryNutrientObject,
  serving_size_in_base_unit: number,
  age_group: AgeGroup,
): CanadaMandatoryNutrientData {
  const mandatory_nutrient_data = {
    FAT: {
      key: CANADA_NUTRITION_KEYS.TOTAL_FAT,
      unit: "g",
      name: "Fat / Lipides",
      per_serve: calculateAndRoundOffCanadaMandatoryNutrientPerServe(
        CANADA_NUTRITION_KEYS.TOTAL_FAT,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
      ),
      daily_value: calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
        CANADA_NUTRITION_KEYS.TOTAL_FAT,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
        age_group,
      ),
    },
    SATURATED: {
      key: CANADA_NUTRITION_KEYS.SATURATED_FAT,
      unit: "g",
      name: "Saturated / saturés",
      per_serve: calculateAndRoundOffCanadaMandatoryNutrientPerServe(
        CANADA_NUTRITION_KEYS.SATURATED_FAT,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
      ),
      daily_value: calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
        CANADA_NUTRITION_KEYS.SATURATED_FAT,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
        age_group,
      ),
    },
    TRANS: {
      key: CANADA_NUTRITION_KEYS.TRANS_FAT,
      unit: "g",
      name: "+Trans / trans",
      per_serve: calculateAndRoundOffCanadaMandatoryNutrientPerServe(
        CANADA_NUTRITION_KEYS.TRANS_FAT,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
      ),
      daily_value: calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
        CANADA_NUTRITION_KEYS.TRANS_FAT,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
        age_group,
      ),
    },
    CARBOHYDRATE: {
      key: CANADA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
      unit: "g",
      name: "Carbohydrates / Glucides",
      per_serve: calculateAndRoundOffCanadaMandatoryNutrientPerServe(
        CANADA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
      ),
      daily_value: calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
        CANADA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
        age_group,
      ),
    },
    FIBRE: {
      key: CANADA_NUTRITION_KEYS.DIETARY_FIBRE,
      unit: "g",
      name: "Fibre / Fibres",
      per_serve: calculateAndRoundOffCanadaMandatoryNutrientPerServe(
        CANADA_NUTRITION_KEYS.DIETARY_FIBRE,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
      ),
      daily_value: calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
        CANADA_NUTRITION_KEYS.DIETARY_FIBRE,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
        age_group,
      ),
    },
    SUGARS: {
      key: CANADA_NUTRITION_KEYS.TOTAL_SUGARS,
      unit: "g",
      name: "Sugars / Sucres",
      per_serve: calculateAndRoundOffCanadaMandatoryNutrientPerServe(
        CANADA_NUTRITION_KEYS.TOTAL_SUGARS,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
      ),
      daily_value: calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
        CANADA_NUTRITION_KEYS.TOTAL_SUGARS,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
        age_group,
      ),
    },
    PROTEIN: {
      key: CANADA_NUTRITION_KEYS.PROTEIN,
      unit: "g",
      name: "Protein / Protéines",
      per_serve: calculateAndRoundOffCanadaMandatoryNutrientPerServe(
        CANADA_NUTRITION_KEYS.PROTEIN,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
      ),
      daily_value: calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
        CANADA_NUTRITION_KEYS.PROTEIN,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
        age_group,
      ),
    },
    CHOLESTEROL: {
      key: CANADA_NUTRITION_KEYS.CHOLESTEROL,
      unit: "g",
      name: "Protein / Protéines",
      per_serve: calculateAndRoundOffCanadaMandatoryNutrientPerServe(
        CANADA_NUTRITION_KEYS.CHOLESTEROL,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
      ),
      daily_value: calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
        CANADA_NUTRITION_KEYS.CHOLESTEROL,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
        age_group,
      ),
    },
    SODIUM: {
      key: CANADA_NUTRITION_KEYS.SODIUM,
      unit: "mg",
      name: "Sodium",
      per_serve: calculateAndRoundOffCanadaMandatoryNutrientPerServe(
        CANADA_NUTRITION_KEYS.SODIUM,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
      ),
      daily_value: calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
        CANADA_NUTRITION_KEYS.SODIUM,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
        age_group,
      ),
    },
    POTASSIUM: {
      key: CANADA_NUTRITION_KEYS.POTASSIUM,
      unit: "mg",
      name: "Potassium",
      per_serve: calculateAndRoundOffCanadaMandatoryNutrientPerServe(
        CANADA_NUTRITION_KEYS.POTASSIUM,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
      ),
      daily_value: calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
        CANADA_NUTRITION_KEYS.POTASSIUM,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
        age_group,
      ),
    },
    CALCIUM: {
      key: CANADA_NUTRITION_KEYS.CALCIUM,
      unit: "mg",
      name: "Calcium",
      per_serve: calculateAndRoundOffCanadaMandatoryNutrientPerServe(
        CANADA_NUTRITION_KEYS.CALCIUM,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
      ),
      daily_value: calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
        CANADA_NUTRITION_KEYS.CALCIUM,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
        age_group,
      ),
    },
    IRON: {
      key: CANADA_NUTRITION_KEYS.IRON,
      unit: "mg",
      name: "Iron / Fer",
      per_serve: calculateAndRoundOffCanadaMandatoryNutrientPerServe(
        CANADA_NUTRITION_KEYS.IRON,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
      ),
      daily_value: calculateAndRoundOffCanadaMandatoryNutrientDailyValues(
        CANADA_NUTRITION_KEYS.IRON,
        serving_size_in_base_unit,
        [filtered_mandatory_nutrient],
        age_group,
      ),
    },
  }

  return mandatory_nutrient_data
}

/**
 * Interleaved nutrients are non-mandatory nutrients that, if selected by the user, are placed between mandatory nutrients.
 * This function returns the interleaved nutrients data for Canada.
 */
export function getCanadaNonMandatoryInterLeavedData(
  serving_size_in_base_unit: number,
  non_mandatory_nutrient: { name: string; quantity: string }[],
): CanadaInterLeavedNonMandatoryNutrientData {
  const interleaved_nutrient_map = {
    POLYUNSATURATED: {
      key: CANADA_NUTRITION_KEYS.PUFA,
      unit: "g",
      name: "Polyunsaturated / polyinsaturés",
    },
    OMEGA_6: {
      key: CANADA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS,
      unit: "g",
      name: "Omega-6 / oméga-6",
    },
    OMEGA_3: {
      key: CANADA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS,
      unit: "g",
      name: "Omega-3 / oméga-3",
    },
    MONOUNSATURATED: {
      key: CANADA_NUTRITION_KEYS.MUFA, // Corrected to MUFA
      unit: "g",
      name: "Monounsaturated / monoinsaturés",
    },
  }

  const result: CanadaInterLeavedNonMandatoryNutrientData = {}

  for (const [label, data] of Object.entries(interleaved_nutrient_map)) {
    const nutrient = non_mandatory_nutrient.find(
      (item) =>
        item.name === data.key &&
        item.quantity !== "" &&
        item.quantity !== null &&
        item.quantity !== undefined,
    )

    if (nutrient) {
      result[label as keyof CanadaInterLeavedNonMandatoryNutrientData] = {
        ...data,
        per_serve:
          calculateAndRoundOffCanadaNonMandatoryInterLeavedNutrientPerServe(
            data.key,
            serving_size_in_base_unit,
            non_mandatory_nutrient,
          ),
        daily_value: "",
      }
    }
  }

  return result
}

/**
 * This function does not calculate MUFA, PUFA, Omega-3, or Omega-6.
 * Use getCanadaNonMandatoryInterLeavedData() to retrieve the data for the above nutrients.
 */
export function getCanadaNonMandatoryNutrientData(
  non_mandatory_nutrient: { name: string; quantity: string }[] = [],
  serving_size_in_base_unit: number,
  age_group: AgeGroup,
): CanadaNutrientData[] {
  const filtered_non_mandatory_nutrient = non_mandatory_nutrient?.filter(
    (nutrient) =>
      nutrient.name?.trim() &&
      nutrient.quantity !== undefined &&
      nutrient.quantity !== null &&
      !INTERLEAVED_NUTRIENTS?.includes(nutrient.name) &&
      nutrient.quantity.trim() !== "",
  )

  return filtered_non_mandatory_nutrient?.map((nutrient) => {
    const nutrient_key = nutrient.name
    const per_100g_value = nutrient.quantity ?? ""

    const nutrient_details =
      CANADA_DV_VALUES?.[age_group as keyof typeof CANADA_DV_VALUES]?.[
        nutrient_key
      ]

    return {
      key: nutrient_key,
      unit: nutrient_details?.unit,
      name: nutrient_details?.nutrient_name,
      per_serve: calculateAndRoundOffCanadaNonMandatoryNutrientPerServe(
        nutrient_key,
        serving_size_in_base_unit,
        per_100g_value,
      ),
      daily_value: calculateAndRoundOffCanadaNonMandatoryNutrientDailyValue(
        per_100g_value,
        nutrient_key,
        serving_size_in_base_unit,
        age_group,
      ),
    }
  })
}

export function sortNonMandatoryNutrientsData(
  data: {
    key: string
    unit: string
    name: string
    per_serve: string | number
    daily_value: string | number
  }[],
  sequence: string[],
) {
  const key_order_map = new Map<string, number>()

  sequence.forEach((key, index) => {
    key_order_map.set(key, index)
  })

  return [...data].sort((a, b) => {
    const aIndex = key_order_map.has(a.key)
      ? key_order_map.get(a.key)!
      : Infinity
    const bIndex = key_order_map.has(b.key)
      ? key_order_map.get(b.key)!
      : Infinity
    return aIndex - bIndex
  })
}
